<template>
  <Teleport to="body">
    <div class="modal-backdrop" @click.self="$emit('close')">
      <div class="modal-container vendor-code-modal">
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">{{ $t('modals.missingVendorCodes') }}</h2>
              <p class="modal-subtitle">{{ $t('modals.missingVendorCodesSubtitle') }}</p>
            </div>
            <button @click="$emit('close')" class="btn btn-icon">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div class="modal-body">
            <div v-if="loading" class="loading-container">
              <div class="spinner-small"></div>
              <span>{{ $t('modals.saving') }}</span>
            </div>

            <div v-else class="vendors-list">
              <div v-for="vendor in vendors" :key="vendor.id" class="vendor-item">
                <div class="vendor-info">
                  <div class="vendor-name">{{ vendor.vendor_name }}</div>
                  <div class="vendor-vat">{{ vendor.vendor_vat || $t('modals.noVAT') }}</div>
                </div>
                <div class="vendor-code-input">
                  <input
                    v-model="vendor.vendor_code"
                    type="text"
                    :placeholder="$t('modals.enterVendorCode')"
                    class="form-control"
                    @keypress.enter="saveVendorCodes"
                  />
                </div>
              </div>
            </div>

            <div v-if="error" class="error-message">
              {{ error }}
            </div>
          </div>

          <div class="modal-footer">
            <button @click="$emit('close')" class="btn btn-secondary">
              {{ $t('modals.cancel') }}
            </button>
            <button @click="saveVendorCodes" class="btn btn-primary" :disabled="loading || !hasChanges">
              <font-awesome-icon v-if="loading" icon="fa-solid fa-spinner" spin />
              {{ $t('modals.saveAndExport') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';

const { t } = useI18n();

const props = defineProps({
  vendors: {
    type: Array,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const loading = ref(false);
const error = ref(null);

const hasChanges = computed(() => {
  return props.vendors.every(vendor => vendor.vendor_code && vendor.vendor_code.trim() !== '');
});

const saveVendorCodes = async () => {
  // Check if all vendors have codes
  const missingCodes = props.vendors.filter(v => !v.vendor_code || v.vendor_code.trim() === '');
  if (missingCodes.length > 0) {
    error.value = `Please add codes for all ${missingCodes.length} vendor(s)`;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Save each vendor code
    const promises = props.vendors.map(vendor => 
      api.put(`/api/vendors/${vendor.id}`, {
        vendor_name: vendor.vendor_name,
        vendor_vat: vendor.vendor_vat,
        vendor_code: vendor.vendor_code
      })
    );

    await Promise.all(promises);
    
    emit('saved');
  } catch (err) {
    console.error('Failed to save vendor codes:', err);
    
    // Display specific error message for vendor_code
    if (err.response?.data?.errors?.vendor_code) {
      error.value = err.response.data.errors.vendor_code[0];
    } else if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else {
      error.value = 'Failed to save vendor codes. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  position: relative;
  z-index: 9999;
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
}

.vendor-code-modal {
  max-width: 700px;
}

.modal-subtitle {
  font-size: 14px;
  color: #6c757d;
  margin-top: 4px;
}

.vendors-list {
  max-height: 400px;
  overflow-y: auto;
}

.vendor-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  gap: 16px;
}

.vendor-item:last-child {
  border-bottom: none;
}

.vendor-info {
  flex: 1;
  min-width: 0;
}

.vendor-name {
  font-weight: 500;
  color: #212529;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vendor-vat {
  font-size: 13px;
  color: #6c757d;
  margin-top: 2px;
}

.vendor-code-input {
  flex: 0 0 200px;
}

.vendor-code-input input {
  width: 100%;
  padding: 6px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.vendor-code-input input:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: #6c757d;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
